import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Trang chủ', path: '/', hasDropdown: false },
    {
      name: 'FIFA Online 4',
      path: '/fifa-online-4',
      hasDropdown: true,
      submenu: [
        { name: 'Tin tức', path: '/fifa-online-4/tin-tuc' },
        { name: 'Hướng dẫn', path: '/fifa-online-4/huong-dan' },
        { name: 'Game thủ', path: '/fifa-online-4/game-thu' }
      ]
    },
    {
      name: 'Age of Empires',
      path: '/age-of-empires',
      hasDropdown: true,
      submenu: [
        { name: 'AOE 1', path: '/age-of-empires/aoe1' },
        { name: 'AOE 2', path: '/age-of-empires/aoe2' },
        { name: 'Giải đấu', path: '/age-of-empires/giai-dau' }
      ]
    },
    { name: 'Liên Minh', path: '/lien-minh-huyen-thoai', hasDropdown: false },
    { name: 'CS:GO', path: '/csgo', hasDropdown: false },
    { name: 'PUBG', path: '/pubg', hasDropdown: false },
    { name: 'Tin Game', path: '/tin-game', hasDropdown: false }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-dark-bg/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="https://public.readdy.ai/ai/img_res/6253707a-d586-4971-a3cf-1e43906464a5.png" 
              alt="GameTV.asia Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
            <span className="text-lg sm:text-2xl font-exo font-black bg-gradient-gaming bg-clip-text text-transparent group-hover:scale-105 transition-transform whitespace-nowrap">
              GameTV.asia
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => item.hasDropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.path}
                  className={`font-inter font-semibold text-sm transition-all whitespace-nowrap ${
                    location.pathname === item.path
                      ? 'text-accent-cyan'
                      : 'text-white hover:text-accent-cyan'
                  }`}
                >
                  {item.name}
                  {item.hasDropdown && <i className="ri-arrow-down-s-line ml-1"></i>}
                </Link>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {item.hasDropdown && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-48 bg-dark-card border border-dark-border rounded-lg shadow-xl overflow-hidden"
                    >
                      {item.submenu?.map((subitem) => (
                        <Link
                          key={subitem.path}
                          to={subitem.path}
                          className="block px-4 py-3 text-sm text-white hover:bg-dark-hover hover:text-accent-cyan transition-colors"
                        >
                          {subitem.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Search & Mobile Menu */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Search Bar */}
            <div className="hidden md:flex items-center bg-dark-border rounded-full px-4 py-2 w-48 lg:w-64">
              <i className="ri-search-line text-gray-400 mr-2 text-sm"></i>
              <input
                type="text"
                placeholder="Tìm kiếm..."
                className="bg-transparent text-white text-sm outline-none w-full placeholder-gray-400"
              />
            </div>

            {/* Mobile Search Button */}
            <button className="md:hidden text-white text-xl w-9 h-9 flex items-center justify-center">
              <i className="ri-search-line"></i>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white text-xl sm:text-2xl w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center"
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 bg-dark-card rounded-lg overflow-hidden border border-gray-800"
            >
              {/* Mobile Search */}
              <div className="md:hidden p-4 border-b border-gray-800">
                <div className="flex items-center bg-dark-border rounded-full px-4 py-2">
                  <i className="ri-search-line text-gray-400 mr-2"></i>
                  <input
                    type="text"
                    placeholder="Tìm kiếm..."
                    className="bg-transparent text-white text-sm outline-none w-full placeholder-gray-400"
                  />
                </div>
              </div>

              {menuItems.map((item) => (
                <div key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-white hover:bg-dark-hover transition-colors font-inter font-semibold"
                  >
                    {item.name}
                  </Link>
                  {item.hasDropdown && item.submenu?.map((subitem) => (
                    <Link
                      key={subitem.path}
                      to={subitem.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-8 py-2 text-sm text-gray-400 hover:bg-dark-hover hover:text-white transition-colors"
                    >
                      {subitem.name}
                    </Link>
                  ))}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;